import { UseQueryOptions, useMutation, useQueryClient } from 'react-query'

import { ExternalIdp, AtInstallation, AtInstallationVersions, Bucket, IntegrationType, S3BucketConfig } from '../types'
import {
  createBucket,
  createExternalIdp,
  deleteBucket,
  deleteDatabricksOAuthU2M,
  deleteWorkloadIdentityFederation,
  generateS3Policy,
  getAtInstallation,
  getAtInstallations,
  getAtLatestVersions,
  getBucket,
  getBuckets,
  getBuiltinAtInstallations,
  getDatabricksOAuthU2M,
  getExternalIdp,
  getExternalIdps,
  getJWK,
  getWorkloadIdentityFederation,
  patchDatabricksOAuthU2M,
  postDatabricksOAuthU2M,
  postWorkloadIdentityFederation,
  updateBucket,
  updateExternalIdp
} from './integrations'
import { queryClient, queryClientRemoveCacheArrayElement, queryClientUpdateCacheArrayElement } from 'queryClient'
import { useCallback, useMemo } from 'react'
import { CartoApiError } from 'features/common/errors'

export const integrationsKeys = {
  buckets: ['integrationBuckets'] as const,
  bucketById: (id: string) => [...integrationsKeys.buckets, id] as const,
  policyByBucketConfig: (config: S3BucketConfig) => [...integrationsKeys.buckets, 's3-policy', config.bucket] as const,

  externalIdps: ['externalIdps'] as const,
  externalIdpById: (id: string) => [...integrationsKeys.externalIdps, id] as const
} as const

export const getBucketsQuery = {
  queryKey: integrationsKeys.buckets,
  queryFn: () => getBuckets(),
  staleTime: Infinity
}

export const getBucketQuery = (bucketId: string) => ({
  queryKey: integrationsKeys.bucketById(bucketId),
  queryFn: async () => {
    const bucket = await getBucket(bucketId)
    if (bucket?.integrationType === IntegrationType.RedshiftImport) {
      const config = bucket.config as S3BucketConfig
      const policy = await queryClient.fetchQuery(generatePolicyQuery(config))
      bucket.config = { ...config, policy: JSON.stringify(policy, null, 2) }
    }
    return bucket
  },
  staleTime: Infinity
})

export const createOrUpdateBucketMutation = {
  mutationFn: (bucket: Bucket) => (bucket.id ? updateBucket(bucket) : createBucket(bucket)),
  onSuccess: (bucket) => {
    queryClientUpdateCacheArrayElement(integrationsKeys.buckets, bucket)
    if (bucket?.id) {
      queryClient.invalidateQueries(integrationsKeys.bucketById(bucket.id))
    }
  }
}

export const deleteBucketMutation = (bucketId) => ({
  mutationFn: () => deleteBucket(bucketId),
  onSuccess: () => {
    queryClientRemoveCacheArrayElement(integrationsKeys.buckets, bucketId)
  }
})

export const getIntegrationsExternalIdpsQuery = {
  queryKey: integrationsKeys.externalIdps,
  queryFn: () => getExternalIdps(),
  staleTime: Infinity
}

export function useIntegrationsExternalIdpQueries(externalIdpId?) {
  const queryClient = useQueryClient()

  const queries = {
    getIntegrationsExternalIdps: getIntegrationsExternalIdpsQuery,

    createExternalIdp: {
      mutationFn: (externalIdp: ExternalIdp) => createExternalIdp(externalIdp),
      onSuccess: () => {
        queryClient.invalidateQueries(integrationsKeys.externalIdps)
      }
    },

    getIntegrationExternalIdp: {
      queryKey: integrationsKeys.externalIdpById(externalIdpId),
      queryFn: async () => getExternalIdp(externalIdpId),
      enabled: !!externalIdpId
    },

    updateExternalIdp: {
      mutationFn: (externalIdp: ExternalIdp) => updateExternalIdp(externalIdp),
      onSuccess: () => {
        queryClient.invalidateQueries(integrationsKeys.externalIdps)
      }
    }
  }

  return { queries }
}

export const atInstallationsKeys = {
  all: ['atInstallations'] as const,
  latestVersions: ['atLatestVersions'] as const
}

export const getAtInstallationsQuery: UseQueryOptions<AtInstallation[], Error> = {
  queryKey: atInstallationsKeys.all,
  queryFn: async () => {
    const [builtin, rest] = await Promise.all([getBuiltinAtInstallations(), getAtInstallations()])
    return [...builtin, ...rest]
  }
}

export const getAtInstallationQuery = (id: string | undefined): UseQueryOptions<AtInstallation, Error> => ({
  queryKey: [atInstallationsKeys.all, id],
  queryFn: async () => getAtInstallation(id as string),
  enabled: Boolean(id)
})

export const getLatestAtVersionsQuery: UseQueryOptions<AtInstallationVersions, Error> = {
  queryKey: [atInstallationsKeys.latestVersions],
  queryFn: async () => getAtLatestVersions()
}

export function generatePolicyQuery(config: S3BucketConfig): UseQueryOptions<Record<string, unknown>, Error, void> {
  return {
    queryKey: integrationsKeys.policyByBucketConfig(config),
    queryFn: () => generateS3Policy(config.bucket),
    staleTime: Infinity
  }
}

const WifKey = ['wif']
export function useWorkloadIdentityFederationQueries() {
  const queryClient = useQueryClient()

  return useMemo(
    () => ({
      getJWK: {
        queryKey: [...WifKey, 'jwk'],
        queryFn: getJWK
      },
      createIntegration: {
        mutationFn: postWorkloadIdentityFederation,
        onSuccess: (response) => {
          queryClient.setQueryData(WifKey, response)
        }
      },
      deleteIntegration: {
        mutationFn: deleteWorkloadIdentityFederation,
        onSuccess: () => {
          queryClient.setQueryData(WifKey, null)
        }
      },
      checkIntegration: {
        queryKey: [...WifKey],
        queryFn: async () => {
          const response = await getWorkloadIdentityFederation()
          return response.audience as string
        }
      }
    }),
    [queryClient]
  )
}

const databricksOAuthU2MKey = ['databricksOAuthU2M']
export function useDatabricksOAuthU2MQueries(enabled = true) {
  const queryClient = useQueryClient()

  return useMemo(
    () => ({
      createIntegration: {
        mutationFn: postDatabricksOAuthU2M,
        onSuccess: (response) => {
          queryClient.setQueryData(databricksOAuthU2MKey, response)
        }
      },
      updateIntegration: {
        mutationFn: patchDatabricksOAuthU2M,
        onSuccess: (response) => {
          queryClient.setQueryData(databricksOAuthU2MKey, response)
        }
      },
      deleteIntegration: {
        mutationFn: deleteDatabricksOAuthU2M,
        onSuccess: () => {
          queryClient.setQueryData(databricksOAuthU2MKey, null)
        }
      },
      checkIntegration: {
        queryKey: [...databricksOAuthU2MKey],
        queryFn: async () => {
          const response = await getDatabricksOAuthU2M()
          return response
        },
        enabled,
        retry: false
      }
    }),
    [queryClient, enabled]
  )
}

export function useCreateWifIntegration() {
  const queries = useWorkloadIdentityFederationQueries()
  return useMutation(queries.createIntegration)
}

export function useDeleteWifIntegration() {
  const queries = useWorkloadIdentityFederationQueries()
  return useMutation(queries.deleteIntegration)
}

export function useWorkloadIdentityFederation() {
  const queryClient = useQueryClient()
  const queries = useWorkloadIdentityFederationQueries()

  const downloadJWK = useCallback(async () => {
    const jwk = await queryClient.fetchQuery(queries.getJWK)
    const blob = new Blob([JSON.stringify(jwk, null, 2)], { type: 'application/json' })
    const blobUrl = URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.href = blobUrl
    link.download = 'jwk.json'
    document.body.appendChild(link)
    link.click()

    // Clean up the URL object and link element
    URL.revokeObjectURL(blobUrl)
    document.body.removeChild(link)
  }, [queryClient, queries])

  return {
    downloadJWK
  }
}

export function useCreateDatabricksOAuthU2MIntegration() {
  const queries = useDatabricksOAuthU2MQueries()
  return useMutation(queries.createIntegration)
}

export function useDeleteDatabricksOAuthU2MIntegration() {
  const queries = useDatabricksOAuthU2MQueries()
  return useMutation<unknown, CartoApiError>(queries.deleteIntegration)
}

export function useUpdateDatabricksOAuthU2MIntegration() {
  const queries = useDatabricksOAuthU2MQueries()
  return useMutation(queries.updateIntegration)
}
